<template>
  <div class="p-grid">
    <div class="p-col-12">
      <Button
        label="Upload"
        icon="pi pi-upload"
        @click="uploadDocument"
        v-bind="$attrs"
      />
    </div>
    <div class="p-col-12">
      <DataTable
        :loading="isFetching"
        :value="attachments"
        class="p-datatable-sm"
      >
        <template #empty="">ไม่มีข้อมูล</template>
        <Column
          header="ID"
          field="id"
          headerStyle="width:4rem"
          bodyStyle="width:4rem"
        ></Column>
        <Column header="ชื่อไฟล์" field="filename"></Column>
        <Column
          header="อัพโหลดเมื่อ"
          field="uploadOn"
          headerStyle="width: 13rem"
          bodyStyle="width: 13rem"
        >
          <template #body="props">
            {{ formatAppDateTime(props.data.uploadOn) }}
          </template>
        </Column>
        <Column
          header="อัพโหลดโดย"
          headerStyle="width: 7rem"
          bodyStyle="width: 7rem"
          field="uploadById"
        ></Column>
        <Column headerStyle="width: 4rem" bodyStyle="width: 4rem">
          <template #body="props">
            <Button
              @click="
                () => onDownloadAttachment(props.data.id, props.data.filename)
              "
              class="p-button-rounded"
              icon="pi pi-download"
            ></Button>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { usePlugin } from "@/plugins";
import {
  GetAttachmentRequest,
  GetAttachmentResponse,
  Attachment,
  UploadAttachmentResponse,
  DownloadAttachmentRequest,
} from "@/types";
import useUpload from "@/upload";
import { saveAs } from "file-saver";

export default defineComponent({
  name: "AttachmentPanel",
  props: {
    documentId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { setLoading, axios, error, formatAppDateTime } = usePlugin();
    const { showUpload } = useUpload();

    const isFetching = ref(false);
    const attachments = ref<Attachment[]>([]);
    const loadDocument = async () => {
      isFetching.value = true;
      try {
        const result = await axios.post<GetAttachmentResponse>(
          "api/v1/services/attachment",
          {
            documentId: props.documentId,
          } as GetAttachmentRequest
        );
        attachments.value = result.data.attachments ?? [];
      } catch (e) {
        error(e);
      } finally {
        isFetching.value = false;
      }
    };

    const uploadDocument = () => {
      showUpload(async (files) => {
        if (!files || files === null) {
          error("ไม่พบ file ที่จะ upload");
          return;
        }
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("documentId", props.documentId);
          const result = await axios.post<UploadAttachmentResponse>(
            "api/v1/services/attachment/upload",
            formData
          );
        }
        loadDocument();
      });
    };

    const onDownloadAttachment = async (id: number, filename: string) => {
      isFetching.value = true;
      try {
        var result = await axios.post(
          "api/v1/services/attachment/download",
          { id: id } as DownloadAttachmentRequest,
          { responseType: "blob" }
        );

        saveAs(result.data, filename);
      } catch (e) {
        error(e);
      } finally {
        isFetching.value = false;
      }
    };

    watch(
      () => props.documentId,
      () => loadDocument()
    );
    onMounted(() => loadDocument());
    return {
      setLoading,
      isFetching,
      attachments,
      uploadDocument,
      onDownloadAttachment,
      formatAppDateTime,
    };
  },
});
</script>

<style></style>
